import React from "react";
import { Container } from "./styles";
import { NavHashLink } from "react-router-hash-link";
import { Typewriter } from "../Typewriter/Typewriter";
import { Magnetic } from "../Magnetic/Magnetic";
import { Parallax } from "../Parallax/Parallax";
import linkedin from '../../assets/linkedin.svg';
import githubIcon from '../../assets/github.svg';
import whatsapp from '../../assets/whatsapp.svg';
import Hello from '../../assets/Hello.webp';
import { ArrowRight } from 'lucide-react';

/* System Status Panel — shows Hirelytics modules as live nodes.
   This is the signature element: specific to Kedar, not interchangeable
   with any other dev portfolio. Shows what he actually built. */
function SystemStatusPanel() {
  const modules = [
    { key: 'student_portal',     label: 'student_portal',     status: 'ACTIVE', color: '#a6da95' },
    { key: 'company_dashboard',  label: 'company_dashboard',  status: 'ACTIVE', color: '#a6da95' },
    { key: 'admin_module',       label: 'admin_module',       status: 'ACTIVE', color: '#a6da95' },
    { key: 'ai_engine',          label: 'ai_engine',          status: 'LIVE',   color: '#8bd5ca' },
    { key: 'email_service',      label: 'email_service',      status: 'ACTIVE', color: '#a6da95' },
  ];

  return (
    <div className="status-panel">
      <div className="panel-header">
        <span className="panel-comment">{"// hirelytics.ai"}</span>
        <span className="panel-subtitle">AI Recruitment Platform</span>
      </div>
      <div className="panel-modules">
        {modules.map((mod) => (
          <div className="module-row" key={mod.key}>
            <span className="module-dot" style={{ background: mod.color }} aria-hidden="true" />
            <span className="module-name">{mod.label}</span>
            <span className="module-status" style={{ color: mod.color }}>[{mod.status}]</span>
          </div>
        ))}
      </div>
      <div className="panel-footer">
        <span className="stat-line">modules: <strong>5 / 5</strong> online</span>
        <span className="stat-line">uptime: <strong>99.9%</strong></span>
      </div>
    </div>
  );
}

export const Hero = React.memo(function Hero() {
  const isNoAnimations = typeof document !== 'undefined' && document.documentElement.classList.contains('no-animations');
  const fadeClass = isNoAnimations ? '' : 'animate-fadeInUp';
  const flipClass = isNoAnimations ? '' : 'animate-flipInX';
  const fadeRightClass = isNoAnimations ? '' : 'animate-fadeInRight';

  return (
    <Container id="home">
      <div className="hero-text">
        {/* Monospace eyebrow — structural device, not just decoration */}
        <div className={fadeClass}>
          <span className="hero-eyebrow">$ whoami</span>
        </div>

        <div className={fadeClass} style={isNoAnimations ? {} : { animationDelay: '100ms' }}>
          <p className="hero-greeting">
            Hello <img src={Hello} alt="Waving hand" width="20" height="20" decoding="async" fetchPriority="high" loading="eager" />, I'm
          </p>
        </div>

        <div className={fadeClass} style={isNoAnimations ? {} : { animationDelay: '200ms' }}>
          <h1 className="gradient-text">Kedar Soni</h1>
        </div>

        <div className={fadeClass} style={isNoAnimations ? {} : { animationDelay: '300ms' }}>
          <span className="open-to-work-badge">
            <span className="signal-dot" aria-hidden="true" />
            Open to Work
          </span>
        </div>

        <div className={fadeClass} style={isNoAnimations ? {} : { animationDelay: '400ms' }}>
          <p className="hero-tagline">
            <Typewriter text="Building Systems, Not Just Projects" delay={600} />
          </p>
        </div>

        <div className={fadeClass} style={isNoAnimations ? {} : { animationDelay: '500ms' }}>
          <p className="hero-description">
            Full-stack developer and entrepreneur building{' '}
            <span className="highlight">AI-powered applications</span>,{' '}
            scalable <span className="highlight">SaaS platforms</span>, and modern web
            experiences focused on{' '}
            <span className="highlight">real-world impact</span>.
          </p>
        </div>

        <div className={flipClass} style={isNoAnimations ? {} : { animationDelay: '600ms' }}>
          <Magnetic strength={0.4}>
            <NavHashLink smooth to="#contact" className="button hero-cta">
              <span>Contact Me</span>
              <ArrowRight size={18} className="arrow-icon" aria-hidden="true" />
            </NavHashLink>
          </Magnetic>
        </div>

        <div className="social-media">
          {[
            { href: 'https://www.linkedin.com/in/kedarsoni/', src: linkedin, alt: 'LinkedIn', label: 'Visit Kedar Soni on LinkedIn', delay: '900ms' },
            { href: 'https://github.com/kedarsoni04', src: githubIcon, alt: 'GitHub', label: 'Visit Kedar Soni on GitHub', delay: '1000ms' },
            { href: 'https://api.whatsapp.com/send?phone=919558392775&text=Hello%20Kedar%20%2C%20I%20found%20your%20contact%20through%20your%20portfolio%20website', src: whatsapp, alt: 'WhatsApp', label: 'Contact Kedar Soni on WhatsApp', delay: '1100ms' },
          ].map(({ href, src, alt, label, delay }) => (
            <div key={alt} className={fadeClass} style={isNoAnimations ? {} : { animationDelay: delay }}>
              <Magnetic>
                <a href={href} target="_blank" rel="noreferrer" aria-label={label}>
                  <img src={src} alt={alt} width="28" height="28" decoding="async" />
                </a>
              </Magnetic>
            </div>
          ))}
        </div>
      </div>

      {/* Right column — System Status Panel replaces generic const developer = {} */}
      <div className="hero-image">
        <div className={fadeRightClass} style={isNoAnimations ? {} : { animationDelay: '700ms' }}>
          <Parallax offset={15}>
            <SystemStatusPanel />
          </Parallax>
        </div>
      </div>
    </Container>
  );
});